import { useMemo } from 'react';

import { useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { cleanEmpty } from 'utils/cleanEmpty';

import { GatewayRulesFiltersChips } from './GatewayRulesFiltersChips';
import { GatewayRulesFiltersForm } from './GatewayRulesFiltersForm';

type GatewayRulesFilterValues = {
  active: boolean | null;
  name: string;
  id: string;
  merchant: string;
  country: string[];
  currency: string[];
  conditionGroup: {
    expressions: {
      clientId: string;
    };
  };
  method: string;
  gateway: string[];
  provider: string;
};

const RESET_VALUES = {
  active: null,
  name: '',
  id: '',
  merchant: '',
  country: [],
  currency: [],
  conditionGroup: {
    expressions: {
      clientId: '',
    },
  },
  provider: '',
  method: '',
  gateway: [],
};

export const GatewayRulesFilters = () => {
  const { filterValues } = useListContext();

  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<GatewayRulesFilterValues>({
      defaultValues: {
        active: filterValues?.active || null,
        name: filterValues?.name || '',
        id: filterValues?.id || '',
        merchant: filterValues?.merchant || '',
        country: filterValues?.country || [],
        currency: filterValues?.currency || [],
        conditionGroup: {
          expressions: {
            clientId: filterValues?.conditionGroup?.expressions?.clientId || '',
          },
        },
        method: filterValues?.['mappings.method'] || '',
        gateway: filterValues?.['mappings.mappingItems.gateway'] || [],
        provider: filterValues?.['mappings.mappingItems.gateway.provider'] || '',
      },
      resetValues: RESET_VALUES,
    });

  const listFilters = useMemo(() => {
    return cleanEmpty(appliedFilters);
  }, [appliedFilters]);

  const isListFilterExist = Object.keys(listFilters).length > 0;

  useSyncFilter({
    appliedFilters,
    transform: (appliedFilters) => {
      const { provider, gateway, method, ...restFilters } = appliedFilters;

      return cleanEmpty({
        ...restFilters,
        'mappings.mappingItems.gateway': gateway || [],
        'mappings.mappingItems.gateway.provider': provider || '',
        'mappings.method': method || '',
      });
    },
  });

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[<FilterButton key="filter" onClick={toggleDrawer(true)} />]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <GatewayRulesFiltersForm />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <GatewayRulesFiltersChips />
            <FilterClearButton
              key="clear"
              onClick={() => {
                onReset();
              }}
              visible={isListFilterExist}
            />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
    </>
  );
};
